import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import type { ApproveResponse } from "../../services/distributionPreviewService";
import type { GenerateStoreResult } from "../../services/distributionService";

/**
 * Vista mostrada tras una aprobación exitosa del preview. Resume el
 * resultado de picking-creator y ofrece navegación a transferencias.
 * Extraído para cumplir P7.
 *
 * Bug fix 2026-05-08 — muestra `skipped_lines` con razón legible para
 * que el operador entienda por qué algunas líneas no llegaron al picking
 * (e.g. dos recos del mismo producto compitiendo por el mismo stock
 * fisico → una se manda y la otra cae a `sin_stock_en_origen`).
 */

interface SkippedRow {
  store_mxp_code: string | null;
  unified_product_id: string;
  reason: string;
}

const REASON_LABEL: Record<string, string> = {
  sin_stock_en_origen: "Sin stock en origen al aprobar",
  excluded_in_preview: "Excluido manualmente en la revisión",
  "pending_capacity:category_cap": "Diferida por cap de categoría",
  "pending_capacity:total_cap": "Diferida por cap total de la tienda",
};

function reasonLabel(reason: string): string {
  return REASON_LABEL[reason] ?? reason;
}

function reasonSeverity(reason: string): "warning" | "info" {
  return reason === "sin_stock_en_origen" ? "warning" : "info";
}

function collectSkipped(
  result: ApproveResponse["result"],
): SkippedRow[] {
  const rows: SkippedRow[] = [];
  if ("per_store" in result) {
    for (const ps of result.per_store) {
      for (const s of ps.skipped_lines) {
        rows.push({
          store_mxp_code: ps.store_mxp_code,
          unified_product_id: s.unified_product_id,
          reason: s.reason,
        });
      }
    }
  } else {
    const single = result as GenerateStoreResult;
    for (const s of single.skipped_lines) {
      rows.push({
        store_mxp_code: single.store_mxp_code,
        unified_product_id: s.unified_product_id,
        reason: s.reason,
      });
    }
  }
  return rows;
}

export function PreviewApprovedView({
  result,
  navigate,
}: {
  result: ApproveResponse;
  navigate: (path: string) => void;
}): React.ReactElement {
  const isBatch = "per_store" in result.result;
  const pickingsCount = result.session.picking_ids_created?.length ?? 0;
  const denials = isBatch
    ? (result.result as { total_denials_created: number })
        .total_denials_created
    : (result.result as { denials_created: number }).denials_created;
  const errors = isBatch
    ? (result.result as { total_errors: number }).total_errors
    : (result.result as { errors: { origin: string; message: string }[] })
        .errors.length;
  const skipped = collectSkipped(result.result);

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Alert severity={errors > 0 ? "warning" : "success"}>
          <Typography variant="h6">Pickings creados en Odoo 18</Typography>
          <Typography variant="body2">
            {pickingsCount} pickings · {denials} denegaciones nuevas · {errors}{" "}
            errores · {skipped.length} línea(s) no enviadas · request_id{" "}
            {result.session.request_id}
          </Typography>
        </Alert>

        {skipped.length > 0 && (
          <Paper variant="outlined">
            <Box sx={{ p: 2, pb: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Líneas no enviadas a Odoo
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Estas recomendaciones quedaron fuera del picking. Las que
                cayeron en <em>sin stock en origen</em> típicamente
                ocurren cuando dos recomendaciones del mismo producto
                compiten por las mismas unidades físicas — la primera se
                envía y la segunda queda en 0.
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tienda</TableCell>
                    <TableCell>Producto</TableCell>
                    <TableCell>Razón</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {skipped.map((row, idx) => (
                    <TableRow key={`${row.unified_product_id}-${idx}`}>
                      <TableCell>{row.store_mxp_code ?? "—"}</TableCell>
                      <TableCell
                        sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}
                      >
                        {row.unified_product_id}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={reasonSeverity(row.reason)}
                          label={reasonLabel(row.reason)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            onClick={() => navigate("/compras/distribucion/transferencias")}
          >
            Ver historial de transferencias
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate("/compras/distribucion")}
          >
            Volver a distribución
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
